import { mapProposalToCapitalGlobalStepPagamentoPrefill } from '@/features/proposals/components/capital-global/utils/map-proposal-to-step-pagamento-prefill.util';
import {
  getCapitalGlobalProposalPremiumAmountLabel,
  getCapitalGlobalProposalPremiumPeriodicity,
} from '@/features/proposals/components/capital-global/steps/constants/capital-global-proposal-payment-step.constants';
import type { CapitalGlobalPremiumPeriodicityOption } from '@/features/proposals/components/capital-global/utils/capital-global-installment-periodicity.util';
import { resolveInstallmentCountFromPremiumPeriodicity } from '@/features/proposals/components/capital-global/utils/capital-global-installment-periodicity.util';
import { resolveCapitalGlobalPaymentMethodLabel } from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-payment-types.hook';
import type { PaymentType } from '@/services';
import { getProposalCategoryLabel } from '@/features/proposals/utils/proposal-category';
import type { Proposal, ProposalCategories } from '@/services/proposals/types';
import {
  PORTAL_STATUS_LABELS,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import { formatCurrency } from '@/shared/utils';
import { getQuoteStatus } from '@/shared/utils/get-quote-status..util';

export type CapitalGlobalProposalResumePremiumData = {
  totalPremium: string;
  liquidPremium: string;
  iof: string;
  paymentPeriodicity: string;
  paymentMethod: string;
  product: string;
  proposalNumber: string;
  dueDay: string;
  status: string;
};

const PORTAL_STATUS_LABEL_OVERRIDES: Partial<Record<number, string>> = {
  [PortalStatus.ProposalInProgress]: 'Proposta em preenchimento',
};

function resolveResumeStatusLabel(proposal: Proposal): string {
  const portalRaw = proposal.statusPortal;
  // `statusPortal` é PortalStatus (enum numérico); a comparação com '' era
  // resquício de quando o campo vinha como string.
  if (portalRaw !== undefined && portalRaw !== null) {
    const portalStatus = Number(portalRaw);
    if (Number.isFinite(portalStatus)) {
      const fromPortal =
        PORTAL_STATUS_LABEL_OVERRIDES[portalStatus] ??
        PORTAL_STATUS_LABELS[portalStatus];
      if (fromPortal) return fromPortal;
    }
  }

  const policyStatus = Number(proposal.policyStatus ?? NaN);
  if (Number.isFinite(policyStatus)) {
    return getQuoteStatus(policyStatus).label;
  }

  return '—';
}

function resolvePaymentPeriodicityLabel(
  proposal: Proposal,
  catalogOptions: CapitalGlobalPremiumPeriodicityOption[] = []
): string {
  const prefill = mapProposalToCapitalGlobalStepPagamentoPrefill(proposal);
  if (!prefill?.premiumPeriodicity) return '—';
  return (
    getCapitalGlobalProposalPremiumPeriodicity(
      prefill.premiumPeriodicity,
      catalogOptions
    )?.label ?? '—'
  );
}

function resolvePaymentMethodLabel(
  proposal: Proposal,
  paymentTypes: PaymentType[] = []
): string {
  const prefill = mapProposalToCapitalGlobalStepPagamentoPrefill(
    proposal,
    paymentTypes
  );
  if (!prefill?.paymentMethod) return '—';
  return resolveCapitalGlobalPaymentMethodLabel(
    prefill.paymentMethod,
    paymentTypes
  );
}

function resolveDueDay(proposal: Proposal): string {
  const prefill = mapProposalToCapitalGlobalStepPagamentoPrefill(proposal);
  return prefill?.bestDueDay?.trim() ? prefill.bestDueDay : '—';
}

function resolveAnnualPremiumAmount(proposal: Proposal): number | null {
  const value = proposal.duePremium ?? proposal.DuePremium;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function resolveInstallmentCount(
  proposal: Proposal,
  catalogOptions: CapitalGlobalPremiumPeriodicityOption[] = []
): number | null {
  const prefill = mapProposalToCapitalGlobalStepPagamentoPrefill(proposal);
  if (!prefill?.premiumPeriodicity) return null;

  const installmentCount = resolveInstallmentCountFromPremiumPeriodicity(
    prefill.premiumPeriodicity,
    catalogOptions
  );

  return installmentCount && installmentCount > 0 ? installmentCount : null;
}

function formatPeriodicPremiumValue(
  value: unknown,
  installmentCount: number | null
): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  if (installmentCount == null) return formatCurrency(numeric);
  return formatCurrency(numeric / installmentCount);
}

function resolveTotalPremiumLabel(
  proposal: Proposal,
  catalogOptions: CapitalGlobalPremiumPeriodicityOption[] = []
): string {
  const prefill = mapProposalToCapitalGlobalStepPagamentoPrefill(proposal);
  return getCapitalGlobalProposalPremiumAmountLabel(
    prefill?.premiumPeriodicity ?? '',
    resolveAnnualPremiumAmount(proposal),
    catalogOptions
  );
}

/**
 * Dados do card de prêmio (step 3 proposta) a partir da policy já calculada.
 */
export function mapProposalToResumePremium(
  proposal: Proposal,
  category: ProposalCategories = 'OTHER',
  paymentTypes: PaymentType[] = [],
  premiumPeriodicityCatalog: CapitalGlobalPremiumPeriodicityOption[] = []
): CapitalGlobalProposalResumePremiumData {
  const installmentCount = resolveInstallmentCount(
    proposal,
    premiumPeriodicityCatalog
  );
  const liquid =
    proposal.rawPremium ??
    proposal.netPremium ??
    (proposal as { PremioLiquido?: number }).PremioLiquido;
  const iofValue =
    proposal.iof ?? (proposal as { IOF?: number }).IOF;

  const proposalNo = proposal.proposalNo?.trim();

  return {
    totalPremium: resolveTotalPremiumLabel(proposal, premiumPeriodicityCatalog),
    liquidPremium: formatPeriodicPremiumValue(liquid, installmentCount),
    iof: formatPeriodicPremiumValue(iofValue, installmentCount),
    paymentPeriodicity: resolvePaymentPeriodicityLabel(
      proposal,
      premiumPeriodicityCatalog
    ),
    paymentMethod: resolvePaymentMethodLabel(proposal, paymentTypes),
    product: getProposalCategoryLabel(category),
    proposalNumber: proposalNo ? `Nº ${proposalNo}` : '—',
    dueDay: resolveDueDay(proposal),
    status: resolveResumeStatusLabel(proposal),
  };
}
